import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { reduxForm } from 'redux-form';
import { useHistory } from 'react-router-dom';
import { Title } from '@app/src/common/Title';
import {
  Description,
  TypeOperation,
  TypeProperty,
  TitleDistrict,
  FormActions
} from '@app/src/views/StepOne/form';
import { FormWrapper } from './styled';
import { setPrintGeneral, GeneralModel } from '@app/src/views/StepTwo/channelPublication';
import { isClientEnv } from '@app/src/utils/validations';
import { clearDistrictFullData } from '@app/src/views/StepOne/form';
import { CATEGORIES } from '@app/src/config/categories';
import { cleanPrintGeneral } from '@app/src/views/StepTwo/channelPublication';

interface Props {
  handleSubmit?: Function;
}

const isClient = isClientEnv();

const Form: React.FC<Props> = ({ handleSubmit }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const currentOperation = useSelector(state => state.form.stepOne.values.operation);
  const currentOperationName = useSelector(state => state.form.stepOne.values.operationName);
  const currentProperty = useSelector(state => state.form.stepOne.values.property);
  const currentPropertyName = useSelector(state => state.form.stepOne.values.propertyName);
  const currentTitle = useSelector(state => state.form.stepOne.values.title);
  const currentDescription = useSelector(state => state.form.stepOne.values.description);
  const currentUbigeo = useSelector(state => state.district);
  const currentDistrictName = useSelector(state => state.form.stepOne.values.districtName);
  const print = isClient && JSON.parse(sessionStorage.getItem('print'));

  useEffect(() => {
    if(currentDescription && isClient) {
      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      }
    }
  }, [currentDescription]);

  const handleBeforeUnload = (e) => {
    if (isClient) {
      sessionStorage.setItem('print', JSON.stringify({general: {category: 1}}));
      e.returnValue = '¿Desea descartar cambios?';
    }
  }

  const handleClickSubmit = (data: GeneralModel) => {
    const updatePrint = createPrintStorage(data);
    isClient && sessionStorage.setItem('print', JSON.stringify(updatePrint));
    dispatch(setPrintGeneral(updatePrint));
    pushDatalayer(
      CATEGORIES.inmueble.name,
      currentOperationName,
      currentPropertyName,
      currentDistrictName
    );
    history.push(`${CATEGORIES.inmueble.link}-2`);
  }

  const quitLastSpace = (text) => {
    let arrText = text.split(' ');
    if (arrText[arrText.length - 1].length === 0) {
      arrText.pop();
    }
    return arrText.join(' ');
  }

  const createPrintStorage = (data) => {
    const objPrint = {};
    if (currentUbigeo && currentUbigeo.hasOwnProperty('ubigeo')) {
      data['ubigeo'] = currentUbigeo.ubigeo;
    } else {
      dispatch(clearDistrictFullData());
    }
    if (print && print.hasOwnProperty('printsByChannel')) {
      objPrint['printsByChannel'] = print.printsByChannel;
    } else {
      objPrint['printsByChannel'] = {};
    }
    data.description = quitLastSpace(data.description);
    objPrint['general'] = data;
    return objPrint;
  }

  const handleClickReset = (e) => {
    e.preventDefault();
    if (currentDescription) {
      const isDiscardChanges = confirm('¿desea descartar cambios?');
      if (!isDiscardChanges) {
        return;
      }
    }
    history.push('/#inicio');
    sessionStorage.removeItem('print');
    dispatch(cleanPrintGeneral());
    dispatch(clearDistrictFullData());
  }

  const pushDatalayer = (categoryName: string, operationName: string, propertyName: string, district: string) => {
    if (isClient && (window as any).dataLayer) {
      (window as any).dataLayer.push({
        'event': 'EVClasificados',
        'eventCategory': 'Posting',
        'eventAction': `Paso1 - ${categoryName}`,
        'eventLabel': `${operationName}|${propertyName}${district && `|${district}`}`,
        'eventValue': 1
      })
    }
  }

  return (
    <FormWrapper>
      <Title>Datos del aviso</Title>
      <TypeOperation />
        {currentOperation && <TypeProperty operation={currentOperation} />}
        {
          (currentOperation && currentProperty) &&
            <TitleDistrict propertyId={currentProperty} />
        }
        {
          currentOperation && currentProperty && currentTitle &&
            <Description
              includeWords={12}
              extraWords={8}
            />
        }
        <FormActions
          categoryConfig={CATEGORIES.inmueble}
          showButtonNext={currentOperation && currentProperty && currentTitle }
          handleButtonNext={handleSubmit(handleClickSubmit)}
          handleButtonPrev={handleClickReset}
        />
    </FormWrapper>
  );
}

const FormHoc = reduxForm({
  form: 'stepOne',
  initialValues: {
    category: null,
    title: '',
    description: '',
    property: null,
    operation: null,
    operationName: '',
    propertyName: '',
    districtName: '',
    extraWords: null,
  }
})(Form);

export { FormHoc as Form };
